/**
 * What M7 writes for a project: the description drafted from the title
 * (Write with M7), and a project named from the first chat.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import type { WorkshopProject } from "@/types/workshop.type";
import { workshopProjectsKey } from "./workshopKeys";

/** Write with M7: one or two plain sentences, a different take when `previous` is given. */
export function useDraftProjectDescription(brandId: string | null) {
  return useMutation({
    mutationFn: async (vars: { title: string; previous?: string }) => {
      const res = await axiosInstance.post<{ description: string }>(
        `/workshop/brand/${brandId}/projects/draft-description`,
        vars,
      );
      return res.data.description;
    },
  });
}

/** The first chat with no project yet: M7 names a new one from the message. */
export function useCreateProjectFromMessage(brandId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (message: string) => {
      const res = await axiosInstance.post<{ project: WorkshopProject }>(
        `/workshop/brand/${brandId}/projects/from-message`,
        { message },
      );
      return res.data.project;
    },
    onSuccess: (project) => {
      qc.setQueryData<WorkshopProject[]>(workshopProjectsKey(brandId), (list = []) =>
        list.some((p) => p.id === project.id)
          ? list
          : [{ ...project, isOwner: true, members: [], sessionCount: 0 }, ...list],
      );
      void qc.invalidateQueries({ queryKey: workshopProjectsKey(brandId) });
    },
  });
}
